<template>
  <div class="md">

    <transition name="md-fade" mode="out-in">

      <!-- ═══════ VISTA: CATEGORÍAS ═══════ -->
      <section v-if="!activeCard" key="cards" class="md-panel">
        <div class="md-panel__head">
          <div>
            <h2 class="md-title">Gestión de datos</h2>
            <p class="md-subtitle">Seleccioná una categoría para visualizar, explorar y exportar la información.</p>
          </div>
          <div class="md-panel__actions">
            <button class="md-btn md-btn--primary" @click="exportB2B" :disabled="b2bExporting">
              <span v-if="b2bExporting" class="md-btn__spinner"></span>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {{ b2bExporting ? 'Generando…' : 'Exportar B2B' }}
            </button>
            <button class="md-btn md-btn--ghost" @click="exportB2C" :disabled="b2cExporting">
              <span v-if="b2cExporting" class="md-btn__spinner md-btn__spinner--dark"></span>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {{ b2cExporting ? 'Generando…' : 'Exportar B2C' }}
            </button>
          </div>
        </div>

        <div class="md-cards-grid">
          <div
            v-for="(card, idx) in cards"
            :key="card.id"
            class="md-card"
            :style="{ '--accent': card.accent, '--i': idx }"
            role="button"
            tabindex="0"
            @click="selectCard(card)"
            @keydown.enter="selectCard(card)"
          >
            <span class="md-card__icon">{{ card.icon }}</span>
            <h3 class="md-card__title">{{ card.title }}</h3>
            <p class="md-card__desc">{{ card.desc }}</p>
            <span class="md-card__cta">
              Ver datos
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </span>
          </div>
        </div>
      </section>

      <!-- ═══════ VISTA: TABLA ═══════ -->
      <section v-else key="table" class="md-panel">

        <!-- Encabezado -->
        <div class="md-detail-head">
          <button class="md-btn md-btn--ghost md-btn--sm" @click="goBack">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Volver
          </button>
          <div class="md-detail-head__title">
            <span class="md-detail-head__icon">{{ activeCard.icon }}</span>
            <div>
              <h2 class="md-detail-head__name">{{ activeCard.title }}</h2>
              <p class="md-detail-head__sub" v-if="!tableLoading">{{ serverTotal }} registro{{ serverTotal !== 1 ? 's' : '' }}</p>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="md-toolbar">
          <div class="md-toolbar__left">
            <input v-model="searchQuery" type="text" class="md-input md-input--search" placeholder="Buscar…" />
          </div>
          <div class="md-toolbar__right">
            <transition name="md-fade">
              <span v-if="autoRefreshing" class="md-refresh-badge">
                <span class="md-refresh-dot"></span> Actualizando
              </span>
            </transition>
            <span class="md-refresh-label" v-if="lastRefreshLabel">{{ lastRefreshLabel }}</span>
            <select v-model.number="itemsPerPage" class="md-input md-input--select">
              <option :value="10">10 / pág</option>
              <option :value="15">15 / pág</option>
              <option :value="25">25 / pág</option>
              <option :value="50">50 / pág</option>
            </select>
            <button class="md-btn md-btn--primary md-btn--sm" :disabled="!tableData.length || tableExporting" @click="exportCurrentTable">
              <span v-if="tableExporting" class="md-btn__spinner"></span>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {{ tableExporting ? 'Generando…' : 'Exportar Excel' }}
            </button>
          </div>
        </div>

        <!-- Cargando -->
        <HyFLoader v-if="tableLoading" variant="inline" />

        <!-- Error -->
        <div v-else-if="tableError" class="md-state md-state--error">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p>{{ tableError }}</p>
          <button class="md-btn md-btn--ghost md-btn--sm" @click="fetchData()">Reintentar</button>
        </div>

        <!-- Tabla -->
        <template v-else-if="tableData.length">
          <div class="md-table-wrap">
            <table class="md-table">
              <thead>
                <tr>
                  <th v-for="col in displayColumns" :key="col">{{ col }}</th>
                  <th v-if="isAccommodationCard" class="md-table__ver-col">Ver</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in tableData"
                  :key="`${currentPage}-${i}`"
                  class="md-table__row"
                >
                  <td v-for="col in displayColumns" :key="col">
                    {{ cellValue(row, col) }}
                  </td>
                  <td v-if="isAccommodationCard">
                    <a
                      :href="accommodationUrl(row)"
                      target="_blank"
                      rel="noopener"
                      class="md-ver-btn"
                      @click.stop
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      Ver
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginación -->
          <div class="md-detail-foot">
            <div class="md-pagination">
              <button class="md-btn md-btn--ghost md-btn--sm" @click="prevGroup" :disabled="pageGroup === 0">‹ Ant.</button>
              <button
                v-for="p in visiblePageNumbers"
                :key="p"
                class="md-page-btn"
                :class="{ 'md-page-btn--active': currentPage === p }"
                @click="goToPage(p)"
              >{{ p }}</button>
              <button class="md-btn md-btn--ghost md-btn--sm" @click="nextGroup" :disabled="pageGroupEnd >= totalPages">Sig. ›</button>
              <span class="md-pagination__info">Pág. {{ currentPage }} / {{ totalPages }}</span>
            </div>
          </div>
        </template>

        <!-- Vacío -->
        <div v-else class="md-state">
          <p>Sin datos disponibles para esta categoría.</p>
        </div>

      </section>
    </transition>

  </div>
</template>

<script>
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import axios from 'axios'
import * as FileSaver from 'file-saver'
import * as XLSX from 'xlsx'

const REFRESH_INTERVAL = 30000

export default {
  components: { HyFLoader },
  name: 'ManageData',

  data() {
    return {
      // Las tarjetas de alojamientos (Aprobados / Eliminados / En Espera) se
      // sacaron a pedido: esas tres APIs se bajaban la tabla de alojamientos
      // entera y no se usaban desde acá.
      cards: [
        { id: 'guests',       icon: '👤', title: 'Usuarios',    desc: 'Todos los usuarios registrados en la plataforma', accent: '#ff7a00', endpoint: 'admin/get-users' },
        { id: 'hosts',        icon: '🏠', title: 'Anfitriones', desc: 'Todos los anfitriones y sus alojamientos',        accent: '#f97316', endpoint: 'admin/get-accommodations', params: { por_anfitrion: 1 } },
        { id: 'reservations', icon: '📋', title: 'Reservas',    desc: 'Historial completo de reservas',                 accent: '#7c3aed', endpoint: 'admin/get-reservations' },
      ],
      activeCard:    null,
      tableData:     [],
      // Total de registros del lado del server: es lo que dice cuántas páginas
      // hay, porque en memoria solo está la página que se está mirando.
      serverTotal:   0,
      tableLoading:  false,
      tableError:    null,
      searchQuery:   '',
      // Término ya "asentado" (con debounce) que se le manda a la API.
      searchTerm:    '',
      currentPage:   1,
      pageGroup:     0,
      itemsPerPage:  15,
      refreshTimer:  null,
      searchTimer:   null,
      autoRefreshing: false,
      lastRefresh:   null,
      b2bExporting:  false,
      b2cExporting:  false,
      tableExporting: false,
    }
  },

  computed: {
    tableColumns() {
      if (!this.tableData.length) return []
      return Object.keys(this.tableData[0])
    },

    // "Anfitriones" es la única tarjeta cuyas filas son alojamientos: de ahí la
    // columna "Ver" y el recorte de columnas de camas.
    isAccommodationCard() {
      return this.activeCard && this.activeCard.id === 'hosts'
    },

    linkColKey() {
      return this.tableColumns.find(c => {
        const cl = c.toLowerCase()
        return cl === 'link' || cl === 'url' || cl === 'enlace' ||
          (cl.includes('link') && !cl.includes('alojamiento')) ||
          (cl.includes('url')  && !cl.includes('alojamiento'))
      }) || null
    },

    displayColumns() {
      // base columns (preserve backend order)
      let cols = this.tableColumns.slice()
      if (!cols.length) return []

      // aplicar filtros para tarjetas de alojamiento (mantener comportamiento existente)
      if (this.isAccommodationCard) {
        cols = cols.filter(col => {
          const cl = col.toLowerCase()
          if (cl.includes('cama') || cl === 'bed_type' || cl === 'tipo_cama') return false
          if (col === this.linkColKey) return false
          return true
        })
      }

      // funciones auxiliares para detectar claves relevantes
      const findKey = (patterns) => {
        for (const k of cols) {
          const kl = k.toLowerCase()
          for (const p of patterns) if (kl.includes(p)) return k
        }
        return null
      }

      const birthPatterns = ['nac', 'birth', 'dob', 'nacimiento']
      const registerPatterns = ['registro', 'registered', 'created', 'created_at', 'signup', 'fecha_registro', 'registrado']
      const lastPatterns = ['ultimo', 'last', 'last_login', 'last_activity', 'last_seen', 'ultimo_acceso', 'updated_at', 'updated', 'modified', 'ultima']

      const birthKey = findKey(birthPatterns)
      if (!birthKey) return cols

      const registerKey = findKey(registerPatterns)
      const lastKey = findKey(lastPatterns)

      // ordenar: mantener todo igual, pero colocar registerKey y lastKey justo después de birthKey
      const orderedAroundBirth = [birthKey]
      if (registerKey && !orderedAroundBirth.includes(registerKey)) orderedAroundBirth.push(registerKey)
      if (lastKey && !orderedAroundBirth.includes(lastKey)) orderedAroundBirth.push(lastKey)

      const idx = cols.indexOf(birthKey)
      if (idx === -1) return cols

      const result = []
      for (let i = 0; i < idx; i++) if (!orderedAroundBirth.includes(cols[i])) result.push(cols[i])
      for (const k of orderedAroundBirth) if (cols.includes(k) && !result.includes(k)) result.push(k)
      for (let i = idx + 1; i < cols.length; i++) if (!orderedAroundBirth.includes(cols[i])) result.push(cols[i])

      return result
    },

    // El orden lo define la API (más nuevo primero) porque, con la tabla
    // paginada, ordenar en el front solo reordenaría la página a la vista.
    totalPages() {
      return Math.max(1, Math.ceil(this.serverTotal / this.itemsPerPage))
    },

    pageGroupEnd() {
      return (this.pageGroup + 1) * 10
    },

    visiblePageNumbers() {
      const start = this.pageGroup * 10 + 1
      const end   = Math.min(start + 9, this.totalPages)
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },

    lastRefreshLabel() {
      if (!this.lastRefresh) return ''
      return `Actualizado ${this.lastRefresh.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`
    },
  },

  watch: {
    // La búsqueda también la resuelve la API: filtrar en el front solo miraría
    // la página cargada. Se espera a que el admin termine de tipear.
    searchQuery(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        const term = value.trim()
        if (term === this.searchTerm) return
        this.searchTerm  = term
        this.currentPage = 1
        this.pageGroup   = 0
        this.fetchData()
      }, 350)
    },
    itemsPerPage() {
      this.currentPage = 1
      this.pageGroup   = 0
      this.fetchData()
    },
  },

  beforeDestroy() {
    this.clearRefreshTimer()
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },

  methods: {
    selectCard(card) {
      this.activeCard  = card
      this.searchQuery = ''
      this.searchTerm  = ''
      this.currentPage = 1
      this.pageGroup   = 0
      this.tableData   = []
      this.serverTotal = 0
      this.tableError  = null
      this.fetchData()
      this.startAutoRefresh()
    },

    goBack() {
      this.activeCard  = null
      this.tableData   = []
      this.serverTotal = 0
      this.tableError  = null
      this.searchQuery = ''
      this.searchTerm  = ''
      this.clearRefreshTimer()
    },

    // Parámetros comunes de la tarjeta activa (filtro propio + búsqueda).
    // `page`/`perPage` se agregan aparte porque la exportación pide todo junto.
    baseParams() {
      const params = { ...(this.activeCard.params || {}) }
      if (this.searchTerm) params.search = this.searchTerm
      return params
    },

    async fetchData(silent = false) {
      if (!this.activeCard) return
      if (!silent) {
        this.tableLoading = true
        this.tableError   = null
      } else {
        this.autoRefreshing = true
      }
      try {
        // Solo se pide la página que se está mirando: el resto de los registros
        // se traen recién cuando el admin toca otra página.
        const res = await axios.get(`${process.env.VUE_APP_API_URL}${this.activeCard.endpoint}`, {
          params: {
            ...this.baseParams(),
            page: this.currentPage,
            perPage: this.itemsPerPage,
          },
        })
        const data = res.data
        if (data && Array.isArray(data.data)) {
          this.tableData   = data.data
          this.serverTotal = Number(data.total) || data.data.length
        } else {
          // Endpoint todavía sin paginar: se muestra lo que haya llegado.
          this.tableData   = Array.isArray(data) ? data : []
          this.serverTotal = this.tableData.length
        }
        this.lastRefresh = new Date()
      } catch (e) {
        console.error('[ManageData] fetchData:', e)
        if (!silent) this.tableError = 'No se pudo cargar la información. Intentá nuevamente.'
      } finally {
        this.tableLoading   = false
        this.autoRefreshing = false
      }
    },

    startAutoRefresh() {
      this.clearRefreshTimer()
      this.refreshTimer = setInterval(() => this.fetchData(true), REFRESH_INTERVAL)
    },

    clearRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    // Cambiar de página es lo que dispara la carga de esa página: nunca se
    // bajan todos los registros de una.
    goToPage(p) {
      if (p < 1 || p > this.totalPages || p === this.currentPage) return
      this.currentPage = p
      this.fetchData()
    },

    prevGroup() {
      if (this.pageGroup > 0) {
        this.pageGroup--
        this.goToPage(this.pageGroup * 10 + 1)
      }
    },

    nextGroup() {
      if (this.pageGroupEnd < this.totalPages) {
        this.pageGroup++
        this.goToPage(this.pageGroup * 10 + 1)
      }
    },

    accommodationUrl(row) {
      // Preferir el link que ya viene del backend si existe
      if (this.linkColKey && row[this.linkColKey]) return row[this.linkColKey]
      // Construir desde el ID
      const id = row['ID'] || row['id'] || row['alojamiento_id'] || ''
      const base = (process.env.VUE_APP_SITE_URL || window.location.origin).replace(/\/$/, '')
      return id ? `${base}/alojamiento/${id}` : '#'
    },

    cellValue(row, col) {
      const val = row[col]
      const colLower = col.toLowerCase()
      const isCountCol =
        colLower.includes('alojamiento') ||
        colLower.includes('reserva') ||
        colLower.includes('accommodation') ||
        colLower.includes('reservation')

      if (isCountCol) {
        if (Array.isArray(val)) return val.length
        if (val === null || val === undefined || val === '') return 0
        if (typeof val === 'string') {
          // puede venir como JSON array o lista separada por comas
          try {
            const parsed = JSON.parse(val)
            if (Array.isArray(parsed)) return parsed.length
          } catch (_) {}
          return val.split(',').filter(v => v.trim() !== '').length
        }
        if (typeof val === 'number') return val
        return 0
      }

      return val !== null && val !== undefined && val !== '' ? val : '—'
    },

    async exportCurrentTable() {
      if (!this.activeCard || this.tableExporting) return
      this.tableExporting = true
      try {
        // En pantalla hay una sola página: para el Excel se pide el listado
        // completo (sin `page`), respetando la búsqueda que esté aplicada.
        const res  = await axios.get(`${process.env.VUE_APP_API_URL}${this.activeCard.endpoint}`, {
          params: this.baseParams(),
        })
        const data = res.data
        const rows = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : [])

        if (!rows.length) {
          alert('No hay registros para exportar.')
          return
        }

        const ws  = XLSX.utils.json_to_sheet(rows)
        const wb  = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, this.activeCard.title.slice(0, 31))
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        FileSaver.saveAs(
          new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `${this.activeCard.title}-${new Date().toISOString().slice(0, 10)}.xlsx`
        )
      } catch (e) {
        console.error('[ManageData] exportCurrentTable:', e)
        alert('No se pudo generar el Excel. Intentá nuevamente.')
      } finally {
        this.tableExporting = false
      }
    },

    async exportB2B() {
      this.b2bExporting = true
      try {
        const res  = await axios.get(`${process.env.VUE_APP_API_URL}admin/get-b2b`)
        const rows = Array.isArray(res.data) ? res.data : []

        if (!rows.length) {
          alert('No se encontraron anfitriones B2B (más de 20 alojamientos aprobados).')
          return
        }

        const wb = XLSX.utils.book_new()

        // ── Hoja 1: todos los alojamientos B2B ──
        const allSheet = XLSX.utils.json_to_sheet(rows.map(r => {
          const { 'ID Anfitrion': _uid, ...rest } = r
          return rest
        }))
        XLSX.utils.book_append_sheet(wb, allSheet, 'Todos B2B')

        // ── Hojas por anfitrión ──
        const byHost = {}
        for (const row of rows) {
          const uid = row['ID Anfitrion']
          if (!byHost[uid]) byHost[uid] = { name: row['Anfitrion'] || String(uid), rows: [] }
          const { 'ID Anfitrion': _uid, ...rest } = row
          byHost[uid].rows.push(rest)
        }

        for (const { name, rows: hostRows } of Object.values(byHost)) {
          const safeName = String(name)
            .replace(/[\\\/*?[\]:]/g, '_')
            .slice(0, 31)
          const ws = XLSX.utils.json_to_sheet(hostRows)
          XLSX.utils.book_append_sheet(wb, ws, safeName || 'Anfitrion')
        }

        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        FileSaver.saveAs(
          new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `B2B-Alojamientos-${new Date().toISOString().slice(0, 10)}.xlsx`
        )
      } catch (e) {
        console.error('[ManageData] exportB2B:', e)
        alert('No se pudo generar el reporte B2B. Intentá nuevamente.')
      } finally {
        this.b2bExporting = false
      }
    },

    async exportB2C() {
      this.b2cExporting = true
      try {
        // Intentar endpoint dedicado primero
        let rows = []
        try {
          const res = await axios.get(`${process.env.VUE_APP_API_URL}admin/get-b2c`)
          rows = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.data) ? res.data.data : [])
        } catch (err) {
          // fallback: obtener todos los alojamientos y agrupar por anfitrión
          const resAcc = await axios.get(`${process.env.VUE_APP_API_URL}admin/get-accommodations`)
          const acc = Array.isArray(resAcc.data) ? resAcc.data : (resAcc.data && Array.isArray(resAcc.data.data) ? resAcc.data.data : [])
          if (!acc.length) {
            alert('No se encontraron registros para generar B2C.')
            return
          }

          // detectar claves de anfitrión
          const sample = acc[0] || {}
          const keys = Object.keys(sample)
          const findKey = (candidates) => keys.find(k => candidates.some(p => k.toLowerCase().includes(p.toLowerCase()))) || null
          const hostIdKey = findKey(['ID Anfitrion','id anfitrion','id_anfitrion','host_id','user_id','owner_id','id_usuario','hostid']) || keys.find(k => /host|anfitr|owner|user/i.test(k))
          const hostNameKey = findKey(['Anfitrion','anfitrion','Host','host','nombre','name','username']) || keys.find(k => /name|nombre|anfitrion|host|user/i.test(k))

          const byHost = {}
          for (const row of acc) {
            const uid = row[hostIdKey] || row['ID Anfitrion'] || row['id'] || row['ID'] || null
            const uidKey = uid != null ? String(uid) : '__unknown__'
            if (!byHost[uidKey]) {
              const displayName = (hostNameKey && row[hostNameKey]) ? row[hostNameKey] : uidKey
              byHost[uidKey] = { name: displayName, rows: [] }
            }
            const rcopy = { ...row }
            if (!rcopy['ID Anfitrion']) rcopy['ID Anfitrion'] = uid
            if (!rcopy['Anfitrion'] && hostNameKey && row[hostNameKey]) rcopy['Anfitrion'] = row[hostNameKey]
            byHost[uidKey].rows.push(rcopy)
          }

          // filtrar anfitriones con menos de 20 alojamientos
          rows = []
          for (const host of Object.values(byHost)) {
            if (host.rows.length < 20) rows.push(...host.rows)
          }
        }

        if (!rows.length) {
          alert('No se encontraron anfitriones B2C (menos de 20 alojamientos).')
          return
        }

        const wb = XLSX.utils.book_new()

        // Hoja: todos los alojamientos B2C
        const allSheet = XLSX.utils.json_to_sheet(rows.map(r => {
          const { 'ID Anfitrion': _uid, ...rest } = r
          return rest
        }))
        XLSX.utils.book_append_sheet(wb, allSheet, 'Todos B2C')

        // Hojas por anfitrión
        const byHostFinal = {}
        for (const row of rows) {
          const uid = row['ID Anfitrion'] || row['id'] || row['host_id'] || row['user_id'] || ''
          const name = row['Anfitrion'] || row['Anfitrión'] || row['Host'] || String(uid)
          if (!byHostFinal[uid]) byHostFinal[uid] = { name, rows: [] }
          const { 'ID Anfitrion': _a, ...rest } = row
          byHostFinal[uid].rows.push(rest)
        }

        for (const { name, rows: hostRows } of Object.values(byHostFinal)) {
          const safeName = String(name).replace(/[\\\/*?[\]:]/g, '_').slice(0, 31)
          const ws = XLSX.utils.json_to_sheet(hostRows)
          XLSX.utils.book_append_sheet(wb, ws, safeName || 'Anfitrion')
        }

        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        FileSaver.saveAs(
          new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `B2C-Alojamientos-${new Date().toISOString().slice(0, 10)}.xlsx`
        )
      } catch (e) {
        console.error('[ManageData] exportB2C:', e)
        alert('No se pudo generar el reporte B2C. Intentá nuevamente.')
      } finally {
        this.b2cExporting = false
      }
    },
  },
}
</script>

<style scoped>
/* ════════════════════════════════════════════════════════
   Sistema de diseño (alineado con BackOffice)
════════════════════════════════════════════════════════ */
.md {
  --c-primary:   #F97316;
  --c-secondary: #EA580C;
  --c-bg:        #F9FAFB;
  --c-card:      #FFFFFF;
  --c-text:      #111827;
  --c-muted:     #6B7280;
  --c-border:    #E5E7EB;
  --c-success:   #10B981;
  --c-warning:   #F59E0B;
  --c-danger:    #EF4444;
  --radius-sm:   8px;
  --radius:      12px;
  --radius-lg:   16px;
  --shadow:      0 1px 3px rgba(0,0,0,.07), 0 2px 8px rgba(0,0,0,.05);
  --shadow-md:   0 4px 14px rgba(0,0,0,.10), 0 2px 6px rgba(0,0,0,.06);
  --transition:  0.2s ease;
  color: var(--c-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
}
.md *, .md *::before, .md *::after { box-sizing: border-box; }
.md-panel { animation: md-fade-in .35s ease both; }

/* ── Encabezado de sección ── */
.md-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--c-border);
}
.md-panel__actions { display: flex; gap: 10px; flex-wrap: wrap; }
.md-title    { font-size: 1.35rem; font-weight: 800; color: var(--c-text); margin: 0; letter-spacing: -.01em; }
.md-subtitle { font-size: .88rem; color: var(--c-muted); margin: 6px 0 0; }

/* ── Botones ── */
.md-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: var(--radius-sm);
  font-size: .88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all var(--transition);
  white-space: nowrap;
  line-height: 1;
  font-family: inherit;
}
.md-btn--primary                     { background: var(--c-primary); color: #fff; border-color: var(--c-primary); }
.md-btn--primary:hover:not(:disabled){ background: var(--c-secondary); border-color: var(--c-secondary); transform: translateY(-1px); box-shadow: 0 4px 10px rgba(249,115,22,.35); }
.md-btn--ghost                       { background: transparent; color: var(--c-muted); border-color: var(--c-border); }
.md-btn--ghost:hover:not(:disabled)  { border-color: var(--c-primary); color: var(--c-primary); background: #FFF7ED; }
.md-btn--sm    { padding: 6px 13px; font-size: .8rem; }
.md-btn:disabled { opacity: .45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
.md-btn svg    { flex-shrink: 0; }
.md-btn__spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: md-spin .7s linear infinite;
  flex-shrink: 0;
}
.md-btn__spinner--dark { border-color: rgba(249,115,22,.3); border-top-color: var(--c-primary); }

/* ── Grilla de tarjetas ── */
.md-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 18px;
}
.md-card {
  position: relative;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  padding: 24px;
  cursor: pointer;
  box-shadow: var(--shadow);
  transition: transform var(--transition), box-shadow var(--transition), border-color var(--transition);
  animation: md-card-in .4s ease both;
  animation-delay: calc(var(--i, 0) * 60ms);
  outline: none;
}
.md-card:hover,
.md-card:focus-visible { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: var(--accent, var(--c-primary)); }
.md-card:focus-visible { outline: 2px solid var(--accent, var(--c-primary)); outline-offset: 2px; }
.md-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px; height: 44px;
  border-radius: var(--radius-sm);
  font-size: 1.3rem;
  margin-bottom: 14px;
  background: color-mix(in srgb, var(--accent, #F97316) 12%, #fff);
}
.md-card__title { font-size: 1rem; font-weight: 700; color: var(--c-text); margin: 0 0 6px; }
.md-card__desc  { font-size: .82rem; color: var(--c-muted); margin: 0 0 16px; line-height: 1.5; }
.md-card__cta   { display: inline-flex; align-items: center; gap: 5px; font-size: .82rem; font-weight: 600; color: var(--accent, var(--c-primary)); }
.md-card__cta svg { transition: transform var(--transition); }
.md-card:hover .md-card__cta svg { transform: translateX(3px); }

/* ── Encabezado de detalle (tabla) ── */
.md-detail-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--c-border);
}
.md-detail-head__title { display: flex; align-items: center; gap: 12px; min-width: 0; }
.md-detail-head__icon  { font-size: 1.5rem; flex-shrink: 0; }
.md-detail-head__name  { font-size: 1.1rem; font-weight: 700; color: var(--c-text); margin: 0; overflow-wrap: anywhere; }
.md-detail-head__sub   { font-size: .8rem; color: var(--c-muted); margin: 4px 0 0; }

/* ── Toolbar ── */
.md-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 0;
  border-bottom: 1px solid var(--c-border);
  margin-bottom: 18px;
}
.md-toolbar__left  { flex: 1; min-width: 200px; }
.md-toolbar__right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

/* ── Inputs ── */
.md-input {
  padding: 9px 13px;
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: .88rem;
  color: var(--c-text);
  background: var(--c-card);
  outline: none;
  transition: border-color var(--transition), box-shadow var(--transition);
  font-family: inherit;
}
.md-input:focus       { border-color: var(--c-primary); box-shadow: 0 0 0 3px rgba(249,115,22,.12); }
.md-input--search     { width: 100%; max-width: 340px; }
.md-input--select     { cursor: pointer; }

/* ── Badge de auto-refresh ── */
.md-refresh-badge {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: .78rem; color: var(--c-muted);
  background: var(--c-bg); border: 1px solid var(--c-border);
  border-radius: 99px; padding: 4px 10px; white-space: nowrap;
}
.md-refresh-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--c-primary); animation: md-pulse .9s ease infinite; }
.md-refresh-label { font-size: .78rem; color: #9CA3AF; white-space: nowrap; }

/* ── Tabla ── */
.md-table-wrap {
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  overflow-x: auto;
  overflow-y: hidden;
}
.md-table-wrap::-webkit-scrollbar        { height: 9px; }
.md-table-wrap::-webkit-scrollbar-thumb  { background: #E5E1DA; border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
.md-table-wrap::-webkit-scrollbar-thumb:hover { background: #D6D2CA; }
.md-table-wrap::-webkit-scrollbar-track  { background: transparent; }
.md-table       { width: 100%; min-width: 760px; border-collapse: collapse; font-size: .85rem; }
.md-table thead { background: var(--c-bg); position: sticky; top: 0; z-index: 1; }
.md-table th    { padding: 14px 18px; text-align: left; font-size: .75rem; font-weight: 700; color: var(--c-muted); text-transform: uppercase; letter-spacing: .05em; border-bottom: 1.5px solid var(--c-border); white-space: nowrap; }
.md-table td    { padding: 14px 18px; border-bottom: 1px solid var(--c-border); color: var(--c-text); vertical-align: middle; white-space: nowrap; }
.md-table__row  { transition: background var(--transition); }
.md-table__row:hover { background: #FFF7ED; }
.md-table tbody tr:last-child td { border-bottom: none; }
.md-table__ver-col { width: 88px; }

.md-ver-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--c-primary);
  color: var(--c-primary);
  background: #FFF7ED;
  font-size: .8rem; font-weight: 600;
  text-decoration: none; white-space: nowrap;
  transition: all var(--transition);
}
.md-ver-btn:hover { background: var(--c-primary); color: #fff; box-shadow: 0 4px 10px rgba(249,115,22,.25); }

/* ── Paginación ── */
.md-detail-foot { display: flex; align-items: center; justify-content: flex-end; padding: 18px 0 4px; }
.md-pagination  { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.md-pagination__info { font-size: .82rem; color: var(--c-muted); font-weight: 500; margin-left: 8px; }
.md-page-btn {
  min-width: 36px; height: 34px; padding: 0 10px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--c-border);
  background: var(--c-card);
  color: var(--c-text);
  font-size: .83rem; font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
}
.md-page-btn:hover:not(.md-page-btn--active) { border-color: var(--c-primary); color: var(--c-primary); }
.md-page-btn--active { background: var(--c-primary); color: #fff; border-color: var(--c-primary); box-shadow: 0 4px 10px rgba(249,115,22,.28); }

/* ── Estados (cargando / error / vacío) ── */
.md-state {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 14px; padding: 72px 20px;
  color: var(--c-muted); font-size: .92rem; text-align: center;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
}
.md-state p { margin: 0; }
.md-state--error { color: var(--c-danger); }
.md-spinner-ring {
  width: 40px; height: 40px;
  border: 4px solid #FFE4CC;
  border-top-color: var(--c-primary);
  border-radius: 50%;
  animation: md-spin .7s linear infinite;
}

/* ── Animaciones ── */
@keyframes md-spin  { to { transform: rotate(360deg); } }
@keyframes md-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(.6); } }
@keyframes md-card-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes md-fade-in { from { opacity: 0; } to { opacity: 1; } }

/* Transición entre vistas */
.md-fade-enter-active, .md-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.md-fade-enter    { opacity: 0; transform: translateY(8px); }
.md-fade-leave-to { opacity: 0; transform: translateY(-8px); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .md-panel__head  { flex-direction: column; }
  .md-panel__actions { width: 100%; }
  .md-panel__actions .md-btn { flex: 1 1 auto; }
  .md-toolbar      { flex-direction: column; align-items: stretch; }
  .md-toolbar__right { justify-content: space-between; }
  .md-input--search { max-width: none; }
  .md-cards-grid   { grid-template-columns: 1fr; }
  .md-detail-foot  { justify-content: center; }
}

@media (max-width: 480px) {
  .md-card          { padding: 18px; }
  .md-title         { font-size: 1.15rem; }
  .md-table th,
  .md-table td      { padding: 11px 12px; }
  .md-state         { padding: 48px 16px; }
  /* La paginación se centra y respira: en fila se salía por la derecha. */
  .md-pagination    { justify-content: center; gap: 5px; }
  .md-pagination__info { width: 100%; text-align: center; margin: 4px 0 0; }
  .md-toolbar__right { flex-direction: column; align-items: stretch; }
  .md-toolbar__right .md-btn,
  .md-toolbar__right .md-input--select { width: 100%; }
}

/* ── Accesibilidad: movimiento reducido ── */
@media (prefers-reduced-motion: reduce) {
  .md *, .md *::before, .md *::after {
    animation-duration: .001ms !important;
    transition-duration: .001ms !important;
  }
}

/* (Los estilos antiguos de ManageData fueron reemplazados por el sistema de diseño de BackOffice, arriba.) */
</style>